"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";

export default function HardwareHero() {
  const { t } = useLang();

  return (
    <section className="relative pt-36 pb-20 px-6 overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-redy-orange/6 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-[1200px] mx-auto text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-5">
            {t("Hardware", "Hardware")}
          </div>
          <h1 className="text-5xl md:text-[72px] font-black tracking-tight leading-[1.02] text-redy-charcoal mb-4">
            {t("Your iPad or Android.", "Tu iPad o Android.")}
            <br />
            <span className="text-black/25">{t("Soon, just your phone.", "Pronto, solo tu teléfono.")}</span>
          </h1>
          <p className="text-lg md:text-xl text-redy-secondary max-w-[600px] mx-auto leading-relaxed font-medium mt-6">
            {t(
              "Redy runs on any tablet you already own. Add an optional $249 North terminal for chip and tap — or skip it once our mobile app launches with Tap to Pay on iPhone (coming soon).",
              "Redy corre en cualquier tablet que ya tienes. Agrega un terminal North opcional de $249 para chip y tap — o sáltatelo cuando lance nuestra app móvil con Tap to Pay on iPhone (muy pronto)."
            )}
          </p>
        </motion.div>

        {/* North terminal CSS mockup */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-16 flex justify-center"
        >
          <div className="relative">
            {/* Glow */}
            <div className="absolute inset-0 m-auto w-[280px] h-[280px] bg-redy-orange/12 rounded-full blur-[80px]" />

            {/* Terminal body */}
            <div className="relative w-[180px] bg-[#1C1C1E] rounded-[28px] shadow-[0_40px_100px_rgba(0,0,0,0.35)] border border-white/8 overflow-hidden">
              {/* Screen */}
              <div className="mx-3 mt-3 bg-[#F7F5F1] rounded-[18px] overflow-hidden">
                <div className="px-4 py-5 text-center">
                  <div className="text-[10px] font-bold text-redy-secondary uppercase tracking-widest mb-1">
                    {t("Total", "Total")}
                  </div>
                  <div className="text-[28px] font-black text-redy-charcoal">$18.50</div>
                  <div className="text-[10px] font-bold text-redy-green mt-0.5">
                    {t("Dual pricing", "Dual pricing")}
                  </div>
                </div>
                {/* Tap symbol */}
                <div className="flex justify-center pb-4">
                  <div className="w-10 h-10 border-2 border-redy-orange/30 rounded-full flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-redy-orange/50 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-redy-orange rounded-full" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Keypad area */}
              <div className="px-4 py-4">
                <div className="grid grid-cols-3 gap-2">
                  {["1","2","3","4","5","6","7","8","9","*","0","#"].map((k) => (
                    <div
                      key={k}
                      className="h-6 bg-white/8 rounded-[6px] flex items-center justify-center text-[10px] font-bold text-white/40"
                    >
                      {k}
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom bar */}
              <div className="h-1 bg-gradient-to-r from-redy-red to-redy-orange mx-3 mb-3 rounded-full" />
            </div>

            {/* Floating badge */}
            <motion.div
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="absolute -right-16 top-1/3 bg-white rounded-2xl shadow-xl border border-black/5 px-4 py-3"
            >
              <div className="text-[10px] font-bold text-redy-secondary leading-tight">
                {t("One-time", "Una vez")}
              </div>
              <div className="text-[22px] font-black text-redy-charcoal">$249</div>
            </motion.div>

            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 1.0 }}
              className="absolute -left-20 bottom-1/3 bg-redy-charcoal rounded-2xl shadow-xl px-4 py-3"
            >
              <div className="text-[10px] font-bold text-white/50">
                {t("Yours forever", "Tuyo siempre")}
              </div>
              <div className="text-[14px] font-black text-white mt-0.5">North Terminal</div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
